import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import {
  LogOut, Mail, Briefcase, Building2, Building, X, Volume2, VolumeX, Send, BellRing, Sun, Moon,
  RefreshCw, Download, CheckCircle2, AlertCircle, Camera, Loader2, Trash2, MonitorSmartphone,
  ChevronRight,
} from 'lucide-react';
import { jwtDecode } from 'jwt-decode';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../contexts/AuthContext';
import { getUser, uploadMyAvatar, deleteMyAvatar } from '../../api/chat';
import Avatar from '../chat/Avatar';
import { ImageLightbox } from '../chat/AttachmentBubble';
import AvatarCropModal from './AvatarCropModal';
import {
  selectSoundOnSend,
  selectSoundOnReceive,
  selectProtectIpAddress,
  selectAudioMutedByDefault,
  setSoundOnSend,
  setSoundOnReceive,
  setProtectIpAddress,
  setAudioMutedByDefault,
} from '../../redux/slices/chatPreferences';
import { playSendSound, playReceiveSound } from '../../utils/chat/sounds';
import { useThemeMode } from '../../hooks/useThemeMode';

type UpdateStatus = 'idle' | 'checking' | 'available' | 'downloading' | 'ready' | 'error' | 'up-to-date';

interface UpdaterSnapshot {
  status: UpdateStatus;
  version: string | null;
  percent: number;
  revision: number;
}

declare global {
  interface Window {
    electronAPI?: {
      isElectron: boolean;
      getVersion: () => Promise<string>;
      getUpdateStatus: () => Promise<UpdaterSnapshot>;
      checkForUpdates: () => Promise<void>;
      installUpdate: () => Promise<void>;
      onUpdateAvailable: (cb: (info: { version: string; snapshot?: UpdaterSnapshot }) => void) => () => void;
      onUpdateDownloaded: (cb: (info: { version: string; snapshot?: UpdaterSnapshot }) => void) => () => void;
      onUpdateProgress: (cb: (info: { percent: number; snapshot?: UpdaterSnapshot }) => void) => () => void;
      onUpdateNotAvailable: (cb: () => void) => () => void;
      onUpdateError: (cb: (msg: string) => void) => () => void;
      [key: string]: unknown;
    };
  }
}

interface Props {
  collapsed: boolean;
}

interface Identity {
  designation?: string | null;
  department_name?: string | null;
  role?: string | null;
}

export default function ProfileTab({ collapsed }: Props) {
  const { user, logout, updateAvatar } = useAuth();
  const navigate = useNavigate();
  const [identity, setIdentity] = useState<Identity>({});
  const [open, setOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(user?.avatar_url ?? null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  // Image the user just picked, awaiting crop/confirm in the modal. null = closed.
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null);
  // Whether the enlarged preview (lightbox) of the current photo is open.
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useDispatch();
  const { isDark, toggle: toggleTheme } = useThemeMode();
  const soundOnSend = useSelector(selectSoundOnSend);
  const soundOnReceive = useSelector(selectSoundOnReceive);
  const protectIpAddress = useSelector(selectProtectIpAddress);
  const audioMutedByDefault = useSelector(selectAudioMutedByDefault);

  const isElectron = !!(window.electronAPI?.isElectron);
  const [appVersion, setAppVersion] = useState<string>('');
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus>('idle');
  const [updateVersion, setUpdateVersion] = useState<string>('');
  const [downloadPercent, setDownloadPercent] = useState<number>(0);
  const updaterRevisionRef = useRef(-1);

  // ── Organisation ────────────────────────────────────────────────────────
  // Login.tsx stashes the selected org in localStorage under `gc.org.v1`.
  // For sessions that pre-date that stash (existing tokens), fall back to
  // claims inside the JWT — most TeamLio access tokens carry company_name
  // / organization / head_office_id. Returns '' if nothing is available.
  const [orgName, setOrgName] = useState<string>('');
  useEffect(() => {
    // 1) localStorage (fast path)
    try {
      const raw = window.localStorage.getItem('gc.org.v1');
      if (raw) {
        const parsed = JSON.parse(raw);
        const label = parsed?.company_name || parsed?.organization || '';
        if (typeof label === 'string' && label) { setOrgName(label); return; }
      }
    } catch { /* ignore */ }
    // 2) JWT fallback for older sessions
    try {
      const token = window.localStorage.getItem('accessToken');
      if (!token) return;
      const claims = jwtDecode<Record<string, unknown>>(token);
      const label =
        (typeof claims.company_name === 'string' && claims.company_name) ||
        (typeof claims.organization === 'string' && claims.organization) ||
        (typeof claims.org_name === 'string' && claims.org_name) ||
        (typeof claims.tenant_name === 'string' && claims.tenant_name) ||
        '';
      if (label) setOrgName(label);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!isElectron) return;
    let cancelled = false;
    const applySnapshot = (snapshot?: UpdaterSnapshot) => {
      if (!snapshot || snapshot.revision < updaterRevisionRef.current) return;
      updaterRevisionRef.current = snapshot.revision;
      if (snapshot.version) setUpdateVersion(snapshot.version);
      setDownloadPercent(snapshot.percent);
      setUpdateStatus(snapshot.status);
    };
    window.electronAPI!.getVersion().then(setAppVersion).catch(() => {});
    const offAvail = window.electronAPI!.onUpdateAvailable(({ version, snapshot }) => {
      if (snapshot) {
        applySnapshot(snapshot);
        return;
      }
      setUpdateVersion(version);
      setUpdateStatus('available');
    });
    const offDownloaded = window.electronAPI!.onUpdateDownloaded(({ version, snapshot }) => {
      if (snapshot) {
        applySnapshot(snapshot);
        return;
      }
      setUpdateVersion(version);
      setUpdateStatus('ready');
    });
    const offProgress = window.electronAPI!.onUpdateProgress(({ percent, snapshot }) => {
      if (snapshot) {
        applySnapshot(snapshot);
        return;
      }
      setUpdateStatus('downloading');
      setDownloadPercent(percent);
    });
    const offNotAvail = window.electronAPI!.onUpdateNotAvailable(() => {
      // Only reflect "up to date" when the user explicitly clicked Check.
      setUpdateStatus(s => s === 'checking' ? 'up-to-date' : s);
    });
    const offError = window.electronAPI!.onUpdateError(() => {
      // Background errors are already silenced in main.js; this only fires for
      // user-initiated checks, so always show the error state here.
      setUpdateStatus('error');
    });
    window.electronAPI!.getUpdateStatus().then((snapshot) => {
      if (!cancelled) applySnapshot(snapshot);
    }).catch(() => {});
    return () => {
      cancelled = true;
      offAvail();
      offDownloaded();
      offProgress();
      offNotAvail();
      offError();
    };
  }, [isElectron]);

  const handleCheckForUpdates = async () => {
    if (!window.electronAPI) return;
    setUpdateStatus('checking');
    try {
      await window.electronAPI.checkForUpdates();
      // fallback: if no event fires within 10s, assume up-to-date
      setTimeout(() => {
        setUpdateStatus(s => s === 'checking' ? 'up-to-date' : s);
      }, 10000);
    } catch {
      setUpdateStatus('error');
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const u = await getUser(user.id);
      if (cancelled || !u) return;
      setIdentity({
        designation: u.designation,
        department_name: u.department_name,
        role: u.role,
      });
      const nextAvatar = u.avatar_url ?? null;
      setAvatarUrl(nextAvatar);
      updateAvatar(nextAvatar);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, updateAvatar]);

  const handlePickPhoto = () => {
    setAvatarError(null);
    fileInputRef.current?.click();
  };

  const handlePhotoSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAvatarError('Please choose an image file');
      return;
    }
    setAvatarError(null);
    setPendingPhoto(file); // open the crop/preview modal; upload happens on Save
  };

  // Called by the crop modal with the cropped JPEG. Throws on failure so the
  // modal can surface the error and stay open; closes itself on success.
  const handleCropSave = async (blob: Blob) => {
    const { avatar_url } = await uploadMyAvatar(blob);
    setAvatarUrl(avatar_url);
    updateAvatar(avatar_url);
    setPendingPhoto(null);
  };

  const handleRemovePhoto = async () => {
    setAvatarBusy(true);
    setAvatarError(null);
    try {
      const { avatar_url } = await deleteMyAvatar();
      setAvatarUrl(avatar_url ?? null);
      updateAvatar(avatar_url ?? null);
    } catch {
      setAvatarError("Couldn't remove photo");
    } finally {
      setAvatarBusy(false);
    }
  };

  if (!user) return null;

  const name = user.name || user.email || 'User';

  // Desktop auto-update surfaced as a badge on this avatar so it's visible
  // without opening the popover. In-progress (available/downloading) shows a
  // pulsing amber dot; a fully-downloaded update ready to install shows a red
  // "1". Other states (idle/checking/up-to-date/error) show nothing. In the
  // browser build updateStatus stays 'idle' (listeners only attach under
  // Electron), so neither badge ever renders there.
  const updateInProgress = updateStatus === 'available' || updateStatus === 'downloading';
  const updateReady = updateStatus === 'ready';

  return (
    <>
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="gt-sidebar-item"
          aria-label="Profile"
          title={updateReady ? 'Update ready to install' : updateInProgress ? 'Downloading update…' : (collapsed ? name : undefined)}
          style={{
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 10,
            width: '100%',
          }}
        >
          <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
            <Avatar name={name} size={7} src={avatarUrl} />
            {updateReady && (
              <span
                aria-hidden
                style={{
                  position: 'absolute', top: -4, right: -4,
                  minWidth: 16, height: 16, padding: '0 4px',
                  borderRadius: 8, background: '#ef4444', color: '#fff',
                  fontSize: 10, fontWeight: 700, lineHeight: 1,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid var(--gt-surface-0, #fff)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                }}
              >
                1
              </span>
            )}
            {updateInProgress && (
              <span
                aria-hidden
                className="animate-pulse"
                style={{
                  position: 'absolute', top: -2, right: -2,
                  width: 10, height: 10, borderRadius: '50%',
                  background: '#f59e0b',
                  border: '2px solid var(--gt-surface-0, #fff)',
                }}
              />
            )}
          </span>
          {!collapsed && (
            <span
              className="gt-sidebar-label"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'left',
                flex: 1,
              }}
            >
              {name}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="right"
          align="end"
          sideOffset={12}
          style={{
            width: 280,
            background: 'var(--gt-surface-0)',
            border: '1px solid var(--gt-divider, rgba(0,0,0,.08))',
            borderRadius: 12,
            boxShadow: '0 12px 32px rgba(0,0,0,.14)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 24px)',
            zIndex: 60,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 12px',
              borderBottom: '1px solid var(--gt-divider, rgba(0,0,0,.06))',
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 13 }}>Profile</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                background: 'transparent',
                border: 0,
                padding: 4,
                cursor: 'pointer',
                color: 'var(--gt-text-muted, #6b7280)',
                display: 'flex',
              }}
            >
              <X size={14} />
            </button>
          </div>
          <div
            aria-label="Profile settings"
            style={{
              overflowY: 'auto',
              minHeight: 0,
            }}
          >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '16px 12px',
              borderBottom: '1px solid var(--gt-divider, rgba(0,0,0,.06))',
            }}
          >
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => (avatarUrl ? setPreviewOpen(true) : handlePickPhoto())}
                aria-label={avatarUrl ? 'View photo' : 'Add photo'}
                title={avatarUrl ? 'View photo' : 'Add photo'}
                style={{
                  background: 'transparent', border: 0, padding: 0,
                  borderRadius: '50%', cursor: 'pointer', display: 'block',
                }}
              >
                <Avatar name={name} size={14} src={avatarUrl} />
              </button>
              <button
                type="button"
                onClick={handlePickPhoto}
                disabled={avatarBusy}
                aria-label="Change photo"
                title="Change photo"
                style={{
                  position: 'absolute',
                  right: -2,
                  bottom: -2,
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  border: '2px solid var(--gt-surface-0, #fff)',
                  background: 'var(--gt-accent, #1f4d3f)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: avatarBusy ? 'default' : 'pointer',
                  padding: 0,
                }}
              >
                {avatarBusy ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoSelected}
              style={{ display: 'none' }}
            />
            <div
              style={{
                marginTop: 8,
                fontSize: 15,
                fontWeight: 700,
                textAlign: 'center',
                wordBreak: 'break-word',
              }}
            >
              {name}
            </div>
            <div style={{ marginTop: 4, display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                type="button"
                onClick={handlePickPhoto}
                disabled={avatarBusy}
                style={{
                  background: 'transparent', border: 0, padding: 0,
                  fontSize: 11, fontWeight: 600,
                  color: 'var(--gt-accent, #1f4d3f)',
                  cursor: avatarBusy ? 'default' : 'pointer',
                }}
              >
                {avatarUrl ? 'Change photo' : 'Add photo'}
              </button>
              {avatarUrl && (
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  disabled={avatarBusy}
                  style={{
                    background: 'transparent', border: 0, padding: 0,
                    fontSize: 11, fontWeight: 600,
                    color: '#dc2626',
                    cursor: avatarBusy ? 'default' : 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                  }}
                >
                  <Trash2 size={11} /> Remove
                </button>
              )}
            </div>
            {avatarError && (
              <div style={{ marginTop: 4, fontSize: 11, color: '#dc2626' }}>{avatarError}</div>
            )}
            {(identity.role || user.role) && (
              <span
                style={{
                  marginTop: 6,
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  padding: '2px 8px',
                  borderRadius: 999,
                  color: 'var(--gt-accent, #1f4d3f)',
                  background: 'var(--gt-accent-soft, #e5f3ec)',
                }}
              >
                {identity.role || user.role}
              </span>
            )}
          </div>
          <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {orgName && (
              <ProfileRow icon={<Building size={14} />} label="Organisation" value={orgName} />
            )}
            {user.email && (
              <ProfileRow icon={<Mail size={14} />} label="Email" value={user.email} />
            )}
            {identity.designation && (
              <ProfileRow icon={<Briefcase size={14} />} label="Designation" value={identity.designation} />
            )}
            {identity.department_name && (
              <ProfileRow icon={<Building2 size={14} />} label="Department" value={identity.department_name} />
            )}
          </div>
          <div
            style={{
              borderTop: '1px solid var(--gt-divider, rgba(0,0,0,.06))',
              padding: 8,
            }}
          >
            {/* Every user's own live sessions — self-service, never gated by an
                Access panel (see accessPanels.ts: /active-devices has no key). */}
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/active-devices'); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '8px 10px',
                background: 'transparent',
                border: 0,
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--gt-text-primary, #111827)',
              }}
            >
              <MonitorSmartphone size={14} />
              Active Devices
              <ChevronRight size={13} style={{ marginLeft: 'auto', color: 'var(--gt-text-muted, #6b7280)' }} />
            </button>
          </div>
          <div
            style={{
              borderTop: '1px solid var(--gt-divider, rgba(0,0,0,.06))',
              padding: '10px 14px 12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div
              style={{
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--gt-text-muted, #6b7280)',
                marginBottom: 2,
              }}
            >
              Sounds
            </div>
            <SoundToggle
              icon={<Send size={13} />}
              label="On send"
              enabled={soundOnSend}
              onChange={(v) => {
                dispatch(setSoundOnSend(v));
                if (v) playSendSound();
              }}
            />
            <SoundToggle
              icon={<BellRing size={13} />}
              label="On receive"
              enabled={soundOnReceive}
              onChange={(v) => {
                dispatch(setSoundOnReceive(v));
                if (v) playReceiveSound();
              }}
            />
          </div>
          <div
            style={{
              borderTop: '1px solid var(--gt-divider, rgba(0,0,0,.06))',
              padding: '10px 14px 12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div
              style={{
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--gt-text-muted, #6b7280)',
                marginBottom: 2,
              }}
            >
              Call privacy
            </div>
            <SoundToggle
              icon={<AlertCircle size={13} />}
              label="Protect IP address"
              enabled={protectIpAddress}
              onChange={(v) => dispatch(setProtectIpAddress(v))}
            />
            <div style={{ fontSize: 11, lineHeight: 1.4, color: 'var(--gt-text-muted, #6b7280)' }}>
              Routes call media through our relay; may reduce call quality and use more bandwidth.
            </div>
            <SoundToggle
              icon={<VolumeX size={13} />}
              label="Mute microphone by default"
              enabled={audioMutedByDefault}
              onChange={(v) => dispatch(setAudioMutedByDefault(v))}
            />
          </div>
          <div
            style={{
              borderTop: '1px solid var(--gt-divider, rgba(0,0,0,.06))',
              padding: '10px 14px 12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div
              style={{
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--gt-text-muted, #6b7280)',
                marginBottom: 2,
              }}
            >
              Appearance
            </div>
            <SoundToggle
              icon={isDark ? <Moon size={13} /> : <Sun size={13} />}
              label="Dark mode"
              enabled={isDark}
              onChange={() => toggleTheme()}
              onIcon={<Moon size={13} />}
              offIcon={<Sun size={13} />}
            />
          </div>
          {isElectron && (
            <div
              style={{
                borderTop: '1px solid var(--gt-divider, rgba(0,0,0,.06))',
                padding: '10px 14px 12px 14px',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: 'var(--gt-text-muted, #6b7280)',
                  marginBottom: 8,
                }}
              >
                App Update
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--gt-text-muted, #6b7280)' }}>
                  {updateStatus === 'idle' && appVersion && `v${appVersion}`}
                  {updateStatus === 'checking' && 'Checking…'}
                  {updateStatus === 'up-to-date' && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#16a34a' }}>
                      <CheckCircle2 size={12} /> Up to date (v{appVersion})
                    </span>
                  )}
                  {updateStatus === 'available' && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#2563eb' }}>
                      <Download size={12} /> v{updateVersion} available
                    </span>
                  )}
                  {updateStatus === 'downloading' && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#2563eb' }}>
                      <Download size={12} /> Downloading… {downloadPercent}%
                    </span>
                  )}
                  {updateStatus === 'ready' && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#16a34a' }}>
                      <CheckCircle2 size={12} /> v{updateVersion} ready
                    </span>
                  )}
                  {updateStatus === 'error' && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#dc2626' }}>
                      <AlertCircle size={12} /> Couldn't check — try again
                    </span>
                  )}
                </div>
                {updateStatus === 'ready' ? (
                  <button
                    type="button"
                    onClick={() => window.electronAPI?.installUpdate()}
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '4px 10px',
                      borderRadius: 6,
                      border: 0,
                      cursor: 'pointer',
                      background: '#16a34a',
                      color: '#fff',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Restart & Install
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={updateStatus === 'checking' || updateStatus === 'downloading'}
                    onClick={handleCheckForUpdates}
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '4px 10px',
                      borderRadius: 6,
                      border: '1px solid var(--gt-divider, rgba(0,0,0,.12))',
                      cursor: updateStatus === 'checking' || updateStatus === 'downloading' ? 'default' : 'pointer',
                      background: 'transparent',
                      color: 'var(--gt-text-primary, #111827)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      opacity: updateStatus === 'checking' || updateStatus === 'downloading' ? 0.5 : 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <RefreshCw size={11} />
                    Check
                  </button>
                )}
              </div>
              {updateStatus === 'downloading' && (
                <div
                  style={{
                    marginTop: 8,
                    height: 3,
                    borderRadius: 999,
                    background: 'var(--gt-divider, rgba(0,0,0,.08))',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${downloadPercent}%`,
                      background: '#2563eb',
                      borderRadius: 999,
                      transition: 'width 300ms ease',
                    }}
                  />
                </div>
              )}
            </div>
          )}
          <div style={{ borderTop: '1px solid var(--gt-divider, rgba(0,0,0,.06))', padding: 8 }}>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void logout();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '8px 10px',
                background: 'transparent',
                border: 0,
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 13,
                color: '#dc2626',
                fontWeight: 600,
              }}
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
    {pendingPhoto && (
      <AvatarCropModal
        file={pendingPhoto}
        onSave={handleCropSave}
        onCancel={() => setPendingPhoto(null)}
      />
    )}
    {previewOpen && avatarUrl && (
      <ImageLightbox
        src={avatarUrl}
        fileName={name}
        onClose={() => setPreviewOpen(false)}
      />
    )}
    </>
  );
}

function SoundToggle({
  icon,
  label,
  enabled,
  onChange,
  onIcon,
  offIcon,
}: {
  icon: React.ReactNode;
  label: string;
  enabled: boolean;
  onChange: (next: boolean) => void;
  onIcon?: React.ReactNode;
  offIcon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => onChange(!enabled)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '6px 8px',
        background: 'transparent',
        border: 0,
        borderRadius: 8,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span
        aria-hidden
        style={{
          color: enabled ? 'var(--gt-accent, #1f4d3f)' : 'var(--gt-text-muted, #9ca3af)',
          display: 'flex',
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1, fontSize: 13, color: 'var(--gt-text-primary, #111827)' }}>
        {label}
      </span>
      <span
        aria-hidden
        style={{
          color: enabled ? 'var(--gt-accent, #1f4d3f)' : 'var(--gt-text-muted, #9ca3af)',
          display: 'flex',
          marginRight: 6,
        }}
      >
        {enabled ? (onIcon ?? <Volume2 size={13} />) : (offIcon ?? <VolumeX size={13} />)}
      </span>
      <span
        aria-hidden
        style={{
          width: 30,
          height: 17,
          background: enabled ? 'var(--gt-accent, #1f4d3f)' : '#cbd5e1',
          borderRadius: 999,
          position: 'relative',
          transition: 'background-color 180ms ease',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: enabled ? 15 : 2,
            width: 13,
            height: 13,
            background: '#fff',
            borderRadius: '50%',
            transition: 'left 180ms ease',
            boxShadow: '0 1px 2px rgba(0,0,0,.18)',
          }}
        />
      </span>
    </button>
  );
}

function ProfileRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <span
        aria-hidden
        style={{ color: 'var(--gt-text-muted, #6b7280)', marginTop: 2, flexShrink: 0 }}
      >
        {icon}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: 'var(--gt-text-muted, #6b7280)',
          }}
        >
          {label}
        </div>
        <div style={{ fontSize: 13, color: 'var(--gt-text-primary, #111827)', wordBreak: 'break-word' }}>
          {value}
        </div>
      </div>
    </div>
  );
}
